import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	ProgramOwnerCheckoutFulfillmentDto,
	ServicesProvider,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to update checkout fulfillment configuration for a services provider
 */
export const updateCheckoutFulfillment = async (
	servicesProviderId: string,
	data: ProgramOwnerCheckoutFulfillmentDto,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.checkoutFulfillment(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating checkout fulfillment configuration for a services provider
 */
export const useUpdateCheckoutFulfillment = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ProgramOwnerCheckoutFulfillmentDto) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to update checkout fulfillment without a services provider ID.',
				);
			}
			return updateCheckoutFulfillment(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(
				queries.servicesProviders.detail(data.id).queryKey,
				data,
			);
		},
	});
};
